import { stringSchema } from '~/schemas'
import { currencyCacheKey, normalizeCurrencyCode } from '~/utils/currency'
import { useLocationStore } from '~/stores/location'
import { useActiveEntity } from '~/composables/useActiveEntity'

type FetchApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: Record<string, unknown>
  query?: Record<string, unknown>
  currency?: string
}

/**
 * Paths where a 401 is an answer about the credentials in the body rather than
 * about the session in the header.
 */
const UNAUTHENTICATED_BY_DESIGN = [
  '/login',
  '/register',
  '/forgotten-password',
  '/reset-password',
]

export const fetchApi = async (
  path: string,
  { method = 'GET', body, query, currency }: FetchApiOptions = {},
) => {
  const { $i18n } = useNuxtApp()
  const runtimeConfig = useRuntimeConfig()
  const authToken = useCookie('authToken')
  const currencyCode = path === '/currencies'
    ? null
    : normalizeCurrencyCode(currency ?? useCurrencyStore().code)
  const session = useSessionExpiry()

  const api = stringSchema.url().parse(runtimeConfig.public.apiBaseUrl)
  const authorization = authToken.value

  const headers: any = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-NUXT-APP-SITE-ID': useSite().site.value.siteId,
    'X-NUXT-APP-LOCALE': $i18n.locale.value,
    ...(authorization ? { Authorization: `Bearer ${authorization}` } : {}),
    ...(currencyCode
      ? { 'X-NUXT-APP-CURRENCY': currencyCode }
      : {}),
  };
  if (import.meta.server) {
    const event = useRequestEvent()
    const realIp = event?.node?.req.headers['x-real-ip']
    const forwardedFor = event?.node?.req.headers['x-forwarded-for']
    const ip = realIp || forwardedFor
    if (ip) {
      headers['x-client-real-ip'] = ip
    }
  }

  return await $fetch(`${api}${path}`, {
    method,
    headers: headers,
    body,
    query,
    // One slow API call must not hold the whole SSR render: fail fast and
    // never retry on the server (BOAT-2101). The browser keeps ofetch's
    // defaults so a flaky mobile connection still gets its retry.
    ...(import.meta.server ? { timeout: 5000, retry: 0 } : {}),
    onResponse({ response }) {
      // Every authenticated response carries the moment the session ends, and
      // that moment moves: the server slides the window on each request
      // (BOAT-1898). Reading it here is what keeps the storefront's idea of the
      // session in step with the server's without a single extra call.
      if (!authorization) return

      session.rememberFromHeaders(response.headers)
    },
    onResponseError({ response }) {
      // A 401 on a request that carried a token means that token is finished.
      // Handled once, centrally, because the alternative is every caller
      // discovering it separately and each inventing its own recovery — which
      // is how a customer ends up reloaded out of a half-filled checkout.
      if (!authorization || response.status !== 401) return

      // Except on the endpoints where 401 is the ordinary answer to getting
      // your password wrong. Those already say so themselves, and a stale
      // cookie on the browser is no reason to add a second, confusing message.
      if (UNAUTHENTICATED_BY_DESIGN.some((endpoint) => path.startsWith(endpoint))) return

      session.expired()
    },
  })
}

export const fetchGlobalData = async () => {
  const auth = useAuthStore()
  const menus = useMenusStore()
  const site = useSiteStore()
  const location = useLocationStore()
  const currency = useCurrencyStore()

  const data = await Promise.all([
    auth.fetch(),
    menus.fetch(),
    site.fetch(),
    location.fetch(),
    currency.fetch(),
  ])

  return {
    auth: data[0],
    menus: data[1],
    site: data[2],
  }
}

export const fetchHomepageData = async ()  => {
  const homepage = useHomepageStore()
  const activeEntity = useActiveEntity()
  const data =  await homepage.fetch()

  activeEntity.value = {
    type: 'page',
    entity: data?.page,
    links: {},
  }

  return data
}

export const revalidateGlobalData = () => {
  const menus = useMenusStore()
  const site = useSiteStore()
  const homepage = useHomepageStore()
  const currency = useCurrencyStore()

  return Promise.all([
    homepage.fetch(true),
    menus.fetch(true),
    site.fetch(true),
    currency.fetch(true),
  ])
}


export const queryKey = (mainKey: string, ...otherKeys: unknown[]) => {
  const { $i18n } = useNuxtApp()
  const authToken = useCookie('authToken')

  const key = [
    mainKey,
    useSite().site.value.siteId,
    $i18n.locale.value,
    authToken.value,
    ...otherKeys.map((key) => {
      if (typeof key === 'string') {
        return key
      }

      if (typeof key === 'number' || typeof key === 'boolean') {
        return key.toString()
      }

      return JSON.stringify(key)
    }),
  ].join('__')

  // console.log('KEY:', key)

  return currencyCacheKey(key, useCurrencyStore().code)
}

interface FetchError extends Error {
  status?: number
  statusCode?: number
  response?: {
    status?: number
  }
  data?: {
    message?: string
    errors?: Record<string, string | string[]>
  }
}

export const isFetchApiError = (error: unknown): error is FetchError => {
  return (
    error instanceof Error &&
    error.name === 'FetchError'
  )
}

export const getFetchApiErrorStatus = (error: unknown) => {
  if (!isFetchApiError(error)) return undefined

  return error.status ?? error.statusCode ?? error.response?.status
}

export const displayErrorMessage = (
  error: unknown,
  defaultMessage?: string,
) => {
  const { $toast, $i18n } = useNuxtApp()
  const { t } = $i18n

  let errorMessage = defaultMessage || t('feedback.generic.error')

  if (isFetchApiError(error)) {
    if (error.data?.errors) {
      errorMessage = Object.values(error.data.errors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .join(' ')
    }

    if (error.data?.message) {
      errorMessage = error.data.message
    }
  }

  $toast.error(errorMessage)
}
